/**
 * Format large numbers intelligently (trailing ".0" is dropped).
 * Examples:
 * - 123 -> "123"
 * - 1,234 -> "1.2k"
 * - 12,345 -> "12k"
 * - 123,456 -> "123k"
 * - 2,000,000 -> "2M"
 * - 2,040,000 -> "2.04M"
 * - 1,234,567 -> "1.2M"
 * - 22,040,000 -> "22.04M"
 * - 1,234,567,890 -> "1.2B"
 */
export function formatNumber(num: number): string {
  if (num < 1000) {
    return num.toString();
  }

  if (num < 10000) {
    // 1,000 - 9,999: Show one decimal, drop .0 (1.2k, 2k)
    return `${parseFloat((num / 1000).toFixed(1))}k`;
  }

  if (num < 1000000) {
    // 10,000 - 999,999: Show whole number (10k - 999k)
    return `${Math.floor(num / 1000)}k`;
  }

  if (num < 10000000) {
    // 1M - 9.9M: Show up to 2 significant decimals, drop trailing zeros.
    // 2,000,000 -> "2M", 2,040,000 -> "2.04M", 2,500,000 -> "2.5M"
    const val = num / 1000000;
    const formatted = parseFloat(val.toFixed(2));
    return `${formatted}M`;
  }

  if (num < 1000000000) {
    // 10M - 999M: Show up to 2 decimals, drop trailing zeros.
    // 20,000,000 -> "20M", 22,040,000 -> "22.04M"
    const val = num / 1000000;
    const formatted = parseFloat(val.toFixed(2));
    return `${formatted}M`;
  }

  // 1B+: Show one decimal, drop .0 (1.2B, 2B)
  return `${parseFloat((num / 1000000000).toFixed(1))}B`;
}
